import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Flex,
  Box,
  Icon,
  VStack,
  HStack,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { useSession } from '@/context/SessionProvider'
import { useBalanceStore } from '@/context/UserBalance'
import { useMutation } from '@tanstack/react-query'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { USDC_DENOM, USDC_NEW_DENOM, USDT_DENOM } from '@/constants/constants'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { BsArrowRight } from 'react-icons/bs'
import { MdSwapHoriz } from 'react-icons/md'
import { useAppToast } from '@/hooks/useAppToast'
import { MsgCreateSpotMarketOrder } from '@ninjablaze/sdk-ts'

interface UsdcConversionModalProps {
  onClose: () => void
  autoConvert?: boolean
}

// Market IDs for USDC/USDT to native USDC conversion on Injective
// TODO: These need to be configured with actual market IDs from Injective Exchange
// You can find market IDs using the Injective Exchange API or Explorer
const OLD_USDC_TO_NEW_USDC_MARKET =
  process.env.NEXT_PUBLIC_OLD_USDC_MARKET_ID || '0xce1d6d6cc747beac4df163329c35f91f4d3397d9549c5e2fe6a29ea3a043063a'
const USDT_TO_NEW_USDC_MARKET =
  process.env.NEXT_PUBLIC_USDT_MARKET_ID || '0x5efdcc4b3a949b3fc78c8c2055d1e46f8a6fe8130627012554047fb3a511345b'

/**
 * Rounds a quantity down to 0.01 USDC step
 * Required by Injective spot markets - minimum 1 USDC, step 0.01 USDC
 *
 * @param chainValue - Token amount in token decimals (6 for USDC)
 * @returns Rounded quantity (0.01 USDC precision)
 */
const roundToTickSize = (chainValue: string): string => {
  const STEP_SIZE = 10000n // 0.01 USDC = 10,000 micro units (step)
  const chainValueBN = BigInt(chainValue)

  // Round down to 0.01 USDC increments
  const rounded = (chainValueBN / STEP_SIZE) * STEP_SIZE
  return rounded.toString()
}

export const UsdcConversionModal = ({ onClose, autoConvert = false }: UsdcConversionModalProps) => {
  const { t } = useTranslation()
  const { account, sendTransaction } = useSession()
  const balance = useBalanceStore((state) => state.balance)
  const { onSuccess, onError } = useAppToast()

  const oldUsdcBalance =
    (balance[USDC_DENOM]?.bank && new TokenMoney(balance[USDC_DENOM].bank.chainValue, USDC_DENOM)) ||
    new TokenMoney(0, USDC_DENOM)
  const usdtBalance = USDT_DENOM
    ? (balance[USDT_DENOM]?.bank && new TokenMoney(balance[USDT_DENOM].bank.chainValue, USDC_DENOM)) ||
      new TokenMoney(0, USDC_DENOM)
    : new TokenMoney(0, USDC_DENOM)

  // Round balances down to 0.01 USDC step (minimum 1 USDC)
  const roundedOldUsdc = roundToTickSize(oldUsdcBalance.chainValue.toFixed(0))
  const roundedUsdt = roundToTickSize(usdtBalance.chainValue.toFixed(0))
  const minAmount = 1000000n // 1 USDC minimum

  const hasOldUsdc = BigInt(roundedOldUsdc) >= minAmount
  const hasUsdt = USDT_DENOM && BigInt(roundedUsdt) >= minAmount

  // Create TokenMoney objects for rounded amounts to display in UI
  const roundedOldUsdcAmount = new TokenMoney(roundedOldUsdc, USDC_DENOM)
  const roundedUsdtAmount = new TokenMoney(roundedUsdt, USDT_DENOM)

  const convertMutation = useMutation({
    mutationFn: async () => {
      if (!account) {
        throw new Error('No account connected')
      }

      // Check if market IDs are configured
      if (!OLD_USDC_TO_NEW_USDC_MARKET && !USDT_TO_NEW_USDC_MARKET) {
        throw new Error(
          'Market IDs not configured. Please set NEXT_PUBLIC_OLD_USDC_MARKET_ID and NEXT_PUBLIC_USDT_MARKET_ID environment variables.'
        )
      }

      // Dynamically import the message type to avoid bundle issues

      const messages: any[] = []

      // Convert old USDC if balance exists and market ID is configured
      if (hasOldUsdc && OLD_USDC_TO_NEW_USDC_MARKET) {
        // Use rounded whole USDC amount (no cents)
        messages.push(
          MsgCreateSpotMarketOrder.fromJSON({
            injectiveAddress: account.injectiveAddress,
            subaccountId: '',
            orderType: 1, // BUY
            feeRecipient: 'inj1lqz2chmp8aygcyl6hsgweayz8s879glt83wd5q',
            marketId: OLD_USDC_TO_NEW_USDC_MARKET,
            price: '1.001', // Market orders can use any price
            quantity: roundedOldUsdc,
            triggerPrice: '0',
          })
        )
      }

      // Convert USDT if balance exists and market ID is configured
      if (hasUsdt && USDT_TO_NEW_USDC_MARKET) {
        // Use rounded whole USDC amount (no cents)
        messages.push(
          MsgCreateSpotMarketOrder.fromJSON({
            injectiveAddress: account.injectiveAddress,
            subaccountId: '',
            orderType: 1, // BUY
            feeRecipient: 'inj1lqz2chmp8aygcyl6hsgweayz8s879glt83wd5q',
            marketId: USDT_TO_NEW_USDC_MARKET,
            price: '1.001', // Market orders can use any price
            quantity: roundedUsdt,
            triggerPrice: '0',
          })
        )
      }

      if (messages.length === 0) {
        throw new Error('No tokens to convert or market IDs not configured')
      }

      return await sendTransaction(messages, {
        showSuccess: false,
        showError: false,
        gasMultiplier: 1.5,
      })
    },
    onSuccess: () => {
      onSuccess({
        custom: {
          title: t('usdc-conversion.success-title', { defaultValue: 'Tokens Migrated Successfully' }),
        },
      })
      onClose()
    },
    onError: (error: any) => {
      onError({
        custom: {
          title:
            error?.message ||
            t('usdc-conversion.error-title', {
              defaultValue: 'Migration failed. Please try again.',
            }),
        },
      })
    },
  })

  if (autoConvert && (hasOldUsdc || hasUsdt)) {
    // Auto-convert if user only has old tokens
    convertMutation.mutate()
    return null
  }
  return null

  return (
    <Modal isOpen={true} onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(10px)" />
      <ModalContent mx={4}>
        <ModalHeader>
          <Flex alignItems="center" gap={2}>
            <Icon as={MdSwapHoriz} boxSize={6} color="ninja.200" />
            <Text>{t('usdc-conversion.title', { defaultValue: 'Token Migration Required' })}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            <Box bg="ninja.700" p={4} rounded="xl">
              <Text fontSize="sm" fontWeight="500" color="ninja.100" lineHeight={1.6}>
                {t('usdc-conversion.description', {
                  defaultValue:
                    'Your tokens need to be migrated to the new USDC format. Convert now to continue playing on Ninja Blaze.',
                })}
              </Text>
            </Box>

            {hasOldUsdc && (
              <Box bg="ninja.700" p={4} rounded="xl">
                <Text fontSize="xs" color="ninja.300" mb={3} fontWeight="600">
                  Old USDC → New USDC
                </Text>
                <HStack justify="space-between" align="center">
                  <HStack spacing={2} flex={1}>
                    <FiatOrCryptoAmount value={roundedOldUsdcAmount} />
                  </HStack>
                  <Icon as={BsArrowRight} color="ninja.300" mx={3} flexShrink={0} />
                  <HStack spacing={2} flex={1} justify="flex-end">
                    <FiatOrCryptoAmount value={new TokenMoney(roundedOldUsdcAmount.chainValue, USDC_NEW_DENOM)} />
                  </HStack>
                </HStack>
              </Box>
            )}

            {hasUsdt && (
              <Box bg="ninja.700" p={4} rounded="xl">
                <Text fontSize="xs" color="ninja.300" mb={3} fontWeight="600">
                  USDT → New USDC
                </Text>
                <HStack justify="space-between" align="center">
                  <HStack spacing={2} flex={1}>
                    <FiatOrCryptoAmount value={roundedUsdtAmount} />
                  </HStack>
                  <Icon as={BsArrowRight} color="ninja.300" mx={3} flexShrink={0} />
                  <HStack spacing={2} flex={1} justify="flex-end">
                    <FiatOrCryptoAmount value={new TokenMoney(roundedUsdtAmount.chainValue, USDC_NEW_DENOM)} />
                  </HStack>
                </HStack>
              </Box>
            )}

            <Button
              w="full"
              h={12}
              rounded="xl"
              isLoading={convertMutation.isPending}
              onClick={() => convertMutation.mutate()}
            >
              {t('usdc-conversion.convert-button', { defaultValue: 'Migrate Tokens' })}
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
